import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, Pencil, ArrowLeft, Loader2, HardHat } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "@/components/Layout";
import PpePictogramsRow from "@/components/PpePictograms";
import { canManageSop } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import SopHistoryDialog from "@/components/SopHistoryDialog";
import api, { resolveFileUrl } from "@/lib/api";

async function exportToPdf(sop, setExporting) {
  setExporting(true);
  try {
    // Let React commit the desktop-forced layout (see the `exporting`
    // conditional classNames in the JSX below) before measuring/capturing -
    // a double rAF is the standard reliable way to wait for that layout.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const node = document.getElementById("sop-print-area");

    // Mobile GPUs commonly cap canvas height around 4096-8192px - a long SOP
    // (many steps/photos) rendered at a fixed scale:2 can silently exceed
    // that, which truncates the capture (and therefore the PDF) right where
    // the limit is hit. node.scrollHeight is now accurate (desktop layout is
    // forced via React state, not a simulated viewport/iframe), so pick the
    // highest scale (up to 2x for sharpness) that stays under a safe cap.
    const MAX_CANVAS_HEIGHT = 6000;
    const scale = Math.min(2, MAX_CANVAS_HEIGHT / node.scrollHeight);

    const canvas = await html2canvas(node, { scale, useCORS: true, backgroundColor: "#ffffff" });
    const imgData = canvas.toDataURL("image/jpeg", 0.92);

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a3" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${sop.numero_sop || "SOP"}.pdf`);
    toast.success("PDF exporté avec succès");
  } catch (e) {
    toast.error("Erreur lors de l'export PDF");
  } finally {
    setExporting(false);
  }
}

function ChecklistBox({ title, items, testId, pictograms }) {
  return (
    <div className="border-2 border-primary/40 rounded-md p-3" data-testid={testId}>
      <h3 className="font-heading text-sm font-bold text-primary uppercase tracking-wide mb-2 italic">{title}</h3>
      {pictograms && pictograms.length > 0 && <PpePictogramsRow pictograms={pictograms} />}
      {(!items || items.length === 0) && <p className="text-xs text-muted-foreground italic">Aucun élément</p>}
      <ul className="space-y-1">
        {items?.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-xs">
            <span className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded-sm border-2 border-success bg-success/10 flex items-center justify-center text-success font-bold leading-none">
              ✓
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SopView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sop, setSop] = useState(null);
  const [pictograms, setPictograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [zoomedPhoto, setZoomedPhoto] = useState(null);

  useEffect(() => {
    api
      .get(`/sops/${id}`)
      .then((res) => setSop(res.data))
      .catch(() => toast.error("SOP introuvable"))
      .finally(() => setLoading(false));
    api
      .get("/reference/all")
      .then((res) => setPictograms(res.data.ppe_pictograms || []))
      .catch(() => setPictograms([]));
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!sop) {
    return (
      <Layout>
        <div className="text-center py-16 text-muted-foreground">SOP introuvable</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print" data-testid="sop-view-toolbar">
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-button">
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          <div className="flex gap-2">
            {canManageSop(user, sop) && (
              <Button variant="outline" onClick={() => navigate(`/sops/${id}/edit`)} data-testid="edit-from-view-button">
                <Pencil className="h-4 w-4" />
                Modifier
              </Button>
            )}
            <SopHistoryDialog sopId={id} />
            <Button
              onClick={() => exportToPdf(sop, setExporting)}
              disabled={exporting}
              data-testid="export-pdf-button"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exporting ? "Génération..." : "Export PDF (format A3)"}
            </Button>
          </div>
        </div>

        <div
          id="sop-print-area"
          className={`bg-white border border-border rounded-lg overflow-hidden shadow-sm print:shadow-none print:border-0 ${
            exporting ? "w-[1152px]" : ""
          }`}
          data-testid="sop-print-area"
        >
          {/* Header bar - mimics the SOP A3 template */}
          <div className={`flex border-b-2 border-primary ${exporting ? "flex-row" : "flex-col sm:flex-row"}`}>
            <div
              className={`bg-primary text-primary-foreground px-4 py-3 flex items-center gap-2 ${
                exporting ? "w-56" : "sm:w-56"
              }`}
            >
              <HardHat className="h-6 w-6" />
              <span className="font-heading font-bold text-lg">Opération</span>
            </div>
            <div className="flex-1 bg-primary/90 text-primary-foreground px-4 py-3 flex items-center justify-center">
              <h1 className="font-heading text-xl font-bold text-center">{sop.titre}</h1>
            </div>
            <div
              className={`border-primary-foreground/30 bg-primary text-primary-foreground px-4 py-2 text-xs ${
                exporting ? "w-56 border-l" : "sm:w-56 border-t sm:border-t-0 sm:border-l"
              }`}
            >
              <p className="font-bold uppercase">Mode opératoire</p>
              <p className="mt-1">
                SOP N°: <span className="font-mono" data-testid="sop-view-numero">{sop.numero_sop}</span>
              </p>
            </div>
          </div>

          <div className={`grid text-xs border-b border-border ${exporting ? "grid-cols-4" : "grid-cols-2 sm:grid-cols-4"}`}>
            <div className="px-4 py-2 border-r border-border">
              <span className="font-bold">Créé par : </span>
              {sop.createur || "—"}
            </div>
            <div className="px-4 py-2 border-r border-border">
              <span className="font-bold">Le : </span>
              {sop.date}
            </div>
            <div className="px-4 py-2 border-r border-border">
              <span className="font-bold">Validé par : </span>
              {sop.validateur || "—"}
            </div>
            <div className="px-4 py-2 flex items-center gap-2">
              <Badge data-testid="sop-view-statut-badge">{sop.statut}</Badge>
            </div>
          </div>

          <div className={`flex border-b border-border text-xs ${exporting ? "flex-row" : "flex-col sm:flex-row"}`}>
            <div className={`px-4 py-2 border-r border-border font-bold ${exporting ? "w-40" : "sm:w-40"}`}>
              Site / Service concerné :
            </div>
            <div className="px-4 py-2 font-semibold">
              {sop.site} — {sop.service}
            </div>
          </div>

          {sop.prerequis && (
            <div className={`flex border-b border-border text-xs ${exporting ? "flex-row" : "flex-col sm:flex-row"}`}>
              <div className={`px-4 py-2 border-r border-border font-bold bg-accent/40 ${exporting ? "w-40" : "sm:w-40"}`}>
                Pré-requis :
              </div>
              <div className="px-4 py-2">{sop.prerequis}</div>
            </div>
          )}

          {/* Steps table - Étape | Opération | Photos | Moyens | Risques | Mesures.
              The table has a min-width so columns (especially Photos) never get
              squeezed below their intended size on narrow screens - without this,
              the browser's default img{max-width:100%} reset shrinks photo width
              while the explicit height stays fixed, distorting them. During export,
              the horizontal scroll wrapper is dropped entirely because the whole
              print area is forced to desktop width above, so the table always fits. */}
          <p className={`${exporting ? "hidden" : "sm:hidden"} text-[11px] text-muted-foreground text-center py-1.5 bg-muted/40 no-print`}>
            ← Faites glisser le tableau pour tout voir →
          </p>
          <div className={exporting ? "" : "overflow-x-auto"}>
          <table className="w-full min-w-[900px] text-xs border-collapse" data-testid="sop-view-steps-table">
            <thead>
              <tr className="bg-muted">
                <th className="border border-border px-2 py-2 w-10">Étape</th>
                <th className="border border-border px-2 py-2 text-left w-1/4">Opération étape par étape</th>
                <th className="border border-border px-2 py-2 w-40">Photos</th>
                <th className="border border-border px-2 py-2 text-left w-1/6">Moyens à utiliser</th>
                <th className="border border-border px-2 py-2 text-left w-1/5">Risques liés à l'opération</th>
                <th className="border border-border px-2 py-2 text-left w-1/5">Mesures de prévention</th>
              </tr>
            </thead>
            <tbody>
              {sop.steps.map((step, idx) => {
                const moyens = step.moyens && step.moyens.length > 0 ? step.moyens : [null];
                return moyens.map((m, mIdx) => (
                  <tr key={`${idx}-${mIdx}`} data-testid={`sop-view-step-row-${idx}-${mIdx}`}>
                    {mIdx === 0 && (
                      <td
                        rowSpan={moyens.length}
                        className="border border-border px-2 py-2 text-center font-heading font-bold text-primary-foreground bg-primary align-top"
                      >
                        {step.numero}
                      </td>
                    )}
                    {mIdx === 0 && (
                      <td rowSpan={moyens.length} className="border border-border px-2 py-2 align-top whitespace-pre-line">
                        {step.description}
                      </td>
                    )}
                    {mIdx === 0 && (
                      <td rowSpan={moyens.length} className="border border-border px-2 py-2 align-top">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(step.photos || []).map((url, pIdx) => (
                            <button
                              key={pIdx}
                              type="button"
                              onClick={() => setZoomedPhoto(resolveFileUrl(url))}
                              className="cursor-zoom-in"
                              data-testid={`zoom-photo-button-${idx}-${pIdx}`}
                              title="Cliquer pour agrandir"
                            >
                              <img
                                src={resolveFileUrl(url)}
                                alt={`Étape ${step.numero} photo ${pIdx + 1}`}
                                crossOrigin="anonymous"
                                className="w-[4cm] h-[4cm] object-cover rounded border-2 border-border"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "data:image/svg+xml;utf8," +
                                    encodeURIComponent(
                                      '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" font-size="11" text-anchor="middle" fill="#9ca3af" font-family="sans-serif">Photo indisponible</text></svg>'
                                    );
                                }}
                              />
                            </button>
                          ))}
                          {(!step.photos || step.photos.length === 0) && (
                            <span className="text-muted-foreground italic">—</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="border border-border px-2 py-2 align-top">{m ? m.moyen : "—"}</td>
                    <td className="border border-border px-2 py-2 align-top text-destructive">
                      {m?.risques || ""}
                    </td>
                    <td className="border border-border px-2 py-2 align-top text-success">
                      {m?.mesures_prevention?.join(" — ") || ""}
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
          </div>

          {/* Page 1 bottom: EPI boxes */}
          <div className={`grid gap-3 p-3 ${exporting ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2"}`}>
            <ChecklistBox
              title="EPI & consignes obligatoires sur le site"
              items={sop.epi_obligatoires}
              testId="sop-view-epi-obligatoires"
              pictograms={pictograms}
            />
            <ChecklistBox title="EPI spécifiques" items={sop.epi_specifiques} testId="sop-view-epi-specifiques" />
          </div>

          {/* Page 2 style: checklist grid */}
          <div
            className={`grid gap-3 p-3 border-t-2 border-primary ${
              exporting ? "grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            }`}
          >
            <ChecklistBox title="Localisation du chantier / Coactivité" items={sop.localisation_coactivite} testId="sop-view-localisation" />
            <ChecklistBox title="Matériel / Engins" items={sop.materiel_engins} testId="sop-view-materiel" />
            <div className="space-y-3">
              <ChecklistBox title="Habilitations et/ou Formations nécessaires" items={sop.habilitations} testId="sop-view-habilitations" />
              <ChecklistBox title="Permis nécessaires" items={sop.permis} testId="sop-view-permis" />
            </div>
            <div className="space-y-3">
              <ChecklistBox title="Mesures génériques" items={sop.mesures_generiques} testId="sop-view-mesures-generiques" />
              <ChecklistBox title="Vigilance, énergies à consigner" items={sop.energies_potentielles} testId="sop-view-energies" />
            </div>
          </div>

          {sop.retours_remarques && (
            <div className="p-3 border-t border-border">
              <div className="border-2 border-primary/40 rounded-md p-3">
                <h3 className="font-heading text-sm font-bold text-primary uppercase tracking-wide mb-1 italic">
                  Retours &amp; Remarques chantier vers la préparation
                </h3>
                <p className="text-sm whitespace-pre-line">{sop.retours_remarques}</p>
              </div>
            </div>
          )}

          <div className="px-4 py-2 border-t border-border flex justify-between text-[10px] text-muted-foreground">
            <span>Créé par {sop.created_by_name || "—"}</span>
            <span>Horodatage : {sop.horodateur ? new Date(sop.horodateur).toLocaleString("fr-FR") : "—"}</span>
          </div>
        </div>
      </div>

      <Dialog open={!!zoomedPhoto} onOpenChange={(v) => !v && setZoomedPhoto(null)}>
        <DialogContent
          className="max-w-[95vw] sm:max-w-3xl p-2 bg-background/95 border-none"
          data-testid="photo-zoom-dialog"
        >
          <DialogTitle className="sr-only">Photo agrandie</DialogTitle>
          {zoomedPhoto && (
            <img
              src={zoomedPhoto}
              alt="Photo agrandie"
              className="w-full h-auto max-h-[85vh] object-contain rounded-md"
              data-testid="photo-zoom-image"
            />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
